/**
 * Garment measurements for an item. The category decides whether it's a top
 * or bottoms, so only those fields show; when it can't tell (a dress, a
 * romper), the person picks. Units follow the workspace: inches or centimeters.
 */
import { useState } from "react";
import type { ItemDetail } from "@sable/api-contract";
import { MEASUREMENT_FIELDS, garmentOf, measurementsRecord, unitFor, type Garment } from "@sable/domain";
import { t, tk, type MessageKey } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { useItemMutation } from "../lib/queries.ts";
import { useWs } from "../lib/workspace.tsx";
import { Dialog, Field } from "./ui.tsx";

function label(garment: Garment, key: string): { label: string; hint: string } {
  const base = key === "length" ? `measure.length.${garment}` : `measure.${key}`;
  return { label: t(base as MessageKey), hint: t(`${base}.hint` as MessageKey) };
}

export function MeasurementsDialog({ item, onClose }: { item: ItemDetail; onClose: () => void }) {
  const ws = useWs();
  const unit = unitFor(ws.units);
  const stored = item.measurements;
  const guessed = garmentOf(item.category, item.subcategory);
  const [garment, setGarment] = useState<Garment>(
    (stored.garment === "top" || stored.garment === "bottom" ? stored.garment : guessed) ?? "top");
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(stored).filter(([, v]) => typeof v === "number").map(([k, v]) => [k, String(v)])));
  const save = useItemMutation(ws.id, item.id, (measurements: Record<string, string | number>) =>
    request<ItemDetail>(`/items/${item.id}`, { method: "PATCH", workspace: ws.id, body: { measurements } }));

  const submit = () => {
    const nums = Object.fromEntries(MEASUREMENT_FIELDS[garment].map((k) => {
      const raw = (values[k] ?? "").trim().replace(",", ".");
      return [k, raw === "" ? null : Number(raw)];
    }));
    save.mutate(measurementsRecord(garment, unit, nums), { onSuccess: onClose });
  };

  return (
    <Dialog
      title={t("item.measurementsTitle")}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>{t("app.cancel")}</button>
        <button className="btn btn-primary" disabled={save.isPending} onClick={submit}>
          {save.isPending ? t("app.saving") : t("item.saveMeasurements")}
        </button>
      </>}
    >
      <div className="stack">
        {guessed === null && (
          <div className="chips" role="radiogroup" aria-label={t("item.measureAs")}>
            {(["top", "bottom"] as const).map((g) => (
              <button key={g} type="button" role="radio" className="chip" aria-pressed={garment === g} aria-checked={garment === g} onClick={() => setGarment(g)}>
                {tk("garment", g)}
              </button>
            ))}
          </div>
        )}
        <div className="grid-2">
          {MEASUREMENT_FIELDS[garment].map((k) => {
            const l = label(garment, k);
            return (
              <Field key={k} label={`${l.label} (${unit})`} hint={l.hint}>
                <input className="input num" type="number" inputMode="decimal" min={0} step={unit === "in" ? 0.25 : 0.5}
                  value={values[k] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} />
              </Field>
            );
          })}
        </div>
        {save.isError && <p className="form-error">{save.error.message}</p>}
      </div>
    </Dialog>
  );
}

/** "Top · Pit to pit 21 in · Length 28 in", or null when nothing is measured. */
export function measurementSummary(m: Record<string, unknown>): string | null {
  const garment: Garment = m.garment === "bottom" ? "bottom" : "top";
  const unit = m.unit === "cm" ? "cm" : "in";
  const parts = MEASUREMENT_FIELDS[garment].filter((k) => typeof m[k] === "number")
    .map((k) => `${label(garment, k).label} ${m[k] as number} ${unit}`);
  return parts.length ? [tk("garment", garment), ...parts].join(" · ") : null;
}
